
import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Plane, MapPin, Calendar, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export default function FlightSearch() {
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    // Only load the script once
    if (scriptLoadedRef.current) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://tpemd.com/content?trs=432284&shmarker=646549&locale=en&curr=USD&powered_by=true&border_radius=0&plain=true&color_button=%232681ff&color_button_text=%23ffffff&color_border=%232681ff&promo_id=4132&campaign_id=121';
    script.charset = 'utf-8';
    
    // Add error handling
    script.onerror = () => {
      console.error('Failed to load TravelPayouts flight search widget');
    };

    // Find the target container and append the script
    const container = document.getElementById('travelpayouts-widget');
    if (container) {
      container.appendChild(script);
      scriptLoadedRef.current = true;
    }

    // Cleanup function
    return () => {
      if (container && script.parentNode) {
        container.removeChild(script);
      }
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Flight Search | Trotting the World</title>
        <meta name="description" content="Find and book the best flight deals worldwide. Compare prices from multiple airlines and travel providers to get the best value for your next adventure." />
        <meta property="og:title" content="Flight Search | Trotting the World" />
        <meta property="og:description" content="Find and book the best flight deals worldwide. Compare prices from multiple airlines and travel providers." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="/flights" />
        <meta name="keywords" content="flights, airline tickets, flight booking, cheap flights, travel deals, flight search" />
      </Helmet>

      <div className="container mx-auto px-4 py-4 sm:py-8">
        {/* Header Section - Optimized for mobile */}
        <header className="text-center mb-6 sm:mb-12">
          <div className="flex justify-center items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="p-2 sm:p-3 bg-primary/10 rounded-full">
              <Plane className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            </div>
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold tracking-tight mb-3 sm:mb-4 px-2">
            Find Your Perfect Flight
          </h1>
          <p className="text-sm sm:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
            Search and compare flights from hundreds of airlines and travel sites to find the best deals for your next adventure.
          </p>
        </header>

        {/* Flight Search Widget Container - Mobile optimized */}
        <div className="max-w-6xl mx-auto mb-8 sm:mb-12">
          <Card className="shadow-sm">
            <CardHeader className="pb-3 sm:pb-6">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Plane className="h-4 w-4 sm:h-5 sm:w-5" />
                Flight Search
              </CardTitle>
              <CardDescription className="text-sm">
                Powered by Trip.com - Find the best flight deals worldwide
              </CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6">
              {/* TravelPayouts Widget Container - Mobile optimized */}
              <div 
                id="travelpayouts-widget" 
                className="min-h-[350px] sm:min-h-[400px] flex items-center justify-center overflow-x-auto"
                style={{ width: '100%' }}
              >
                <div className="text-center text-muted-foreground">
                  <Plane className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 animate-pulse" />
                  <p className="text-sm sm:text-base">Loading flight search...</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Information Section - Mobile optimized grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto mb-8 sm:mb-12">
          <Card className="shadow-sm">
            <CardContent className="pt-4 sm:pt-6 p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                <h3 className="font-semibold text-sm sm:text-base">Worldwide Destinations</h3>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Search flights to thousands of destinations across the globe with competitive prices.
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="pt-4 sm:pt-6 p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                <h3 className="font-semibold text-sm sm:text-base">Flexible Dates</h3>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Find the best deals by comparing prices across different dates and times.
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-sm sm:col-span-2 lg:col-span-1">
            <CardContent className="pt-4 sm:pt-6 p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                <h3 className="font-semibold text-sm sm:text-base">Group Booking</h3>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Book for multiple travelers with options for families, couples, and solo adventurers.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Travel Tips Section - Mobile optimized */}
        <div className="max-w-6xl mx-auto">
          <Card className="shadow-sm">
            <CardHeader className="pb-3 sm:pb-6">
              <CardTitle className="text-lg sm:text-xl">Flight Booking Tips</CardTitle>
              <CardDescription className="text-sm">
                Get the most out of your flight search experience
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <h4 className="font-semibold mb-2 text-sm sm:text-base">Best Time to Book</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Book domestic flights 6-8 weeks in advance and international flights 8-12 weeks ahead for the best deals.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-sm sm:text-base">Compare Prices</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Check multiple dates and nearby airports to find the lowest fares for your destination.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-sm sm:text-base">Flexible Travel</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Being flexible with your travel dates can save you hundreds of dollars on flights.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-sm sm:text-base">Clear Your Cache</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Clear your browser cookies or use incognito mode to avoid dynamic pricing.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
